import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/* ============================================================
   VERIFICAÇÃO TRIMESTRAL DOS TRABALHOS EM CURSO
   P_IM_02 — o RSI verifica trimestralmente os trabalhos em curso na
   vertente de qualidade e segurança, com a Check-list de segurança.

   Um registo por obra visitada. Os quatro trimestres estão sempre à
   vista: um trimestre já decorrido e sem verificação fica a laranja,
   que é a leitura que faltava no papel.
   ============================================================ */

const TRIMESTRES = [
    { n: 1, label: '1.º trimestre', meses: 'jan · fev · mar' },
    { n: 2, label: '2.º trimestre', meses: 'abr · mai · jun' },
    { n: 3, label: '3.º trimestre', meses: 'jul · ago · set' },
    { n: 4, label: '4.º trimestre', meses: 'out · nov · dez' },
];

// A posição não chega para identificar a obra (a 14548 tem duas): vai também o nome.
const nomeObra = (v) => (v.obra_posicao
    ? [v.obra_posicao, v.obra_nome].filter(Boolean).join(' · ')
    : (v.obra_descricao || '—'));

/* ---------- Registo fotográfico (item 25 da check-list) ---------- */
const MAX_FOTOS = 30;

// Reduz no browser (máx. 1920 px, JPEG): os 5–8 MB do telemóvel passam a
// ~0,5 MB, a orientação EXIF fica aplicada e os metadados (GPS incluído)
// não seguem. null se o browser não a souber ler (ex.: HEIC no Chrome).
const prepararFoto = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const max = 1920;
        let w = img.naturalWidth, h = img.naturalHeight;
        if (w > max || h > max) {
            const k = max / Math.max(w, h);
            w = Math.round(w * k); h = Math.round(h * k);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; // PNG com transparência não fica a preto
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(b => resolve(b ? { blob: b, nome: `${file.name.replace(/\.[^.]*$/, '') || 'foto'}.jpg` } : null),
            'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
});

// Uma a uma: pedidos pequenos (passam em qualquer proxy) e dá para mostrar o progresso.
async function enviarFotos(verificacaoId, files, onProgresso = () => { }) {
    const falhas = [];
    let enviadas = 0;
    for (let i = 0; i < files.length; i++) {
        onProgresso(`A carregar ${i + 1} de ${files.length}…`);
        const p = await prepararFoto(files[i]);
        if (!p) { falhas.push(`${files[i].name}: o browser não lê este formato (use JPEG ou PNG)`); continue; }
        const fd = new FormData();
        fd.append('fotos', p.blob, p.nome);
        try {
            await api.post(`/verificacoes-obra/${verificacaoId}/fotos`, fd, { timeout: 60000 });
            enviadas++;
        } catch (e) {
            falhas.push(`${files[i].name}: ${e.response?.data?.error || 'erro no envio'}`);
        }
    }
    onProgresso('');
    return { enviadas, falhas };
}

// A fotografia vem pela API (o token vai no cabeçalho), daí o blob.
function Miniatura({ foto, onApagar, onLegenda }) {
    const [src, setSrc] = useState(null); // null = a carregar, '' = falhou
    const [legenda, setLegenda] = useState(foto.legenda || '');

    useEffect(() => {
        let url = null, ativo = true;
        api.get(`/verificacoes-obra/fotos/${foto.id}`, { responseType: 'blob', timeout: 60000 })
            .then(r => {
                url = URL.createObjectURL(r.data);
                if (ativo) setSrc(url); else URL.revokeObjectURL(url);
            })
            .catch(() => { if (ativo) setSrc(''); });
        return () => { ativo = false; if (url) URL.revokeObjectURL(url); };
    }, [foto.id]);

    const gravarLegenda = () => {
        if (legenda.trim() !== (foto.legenda || '')) onLegenda(foto, legenda.trim());
    };

    return (
        <div style={fotoCaixa}>
            <div onClick={() => src && window.open(src, '_blank')} title={src ? 'Abrir em tamanho real' : ''}
                style={{ ...fotoArea, cursor: src ? 'zoom-in' : 'default' }}>
                {src ? <img src={src} alt={foto.legenda || foto.nome_original || ''} style={fotoImg} />
                    : src === '' ? 'não foi possível abrir' : 'a carregar…'}
            </div>
            <button type="button" onClick={() => onApagar(foto)} title="Remover fotografia" style={btnRemoverFoto}><DeleteIcon /></button>
            <input value={legenda} onChange={e => setLegenda(e.target.value)} onBlur={gravarLegenda}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                placeholder="legenda (opcional)" maxLength={300} style={fotoLegenda} />
        </div>
    );
}

// Numa verificação ainda por gravar: pré-visualização local até ao «Guardar».
function PreviewLocal({ file, onRemover }) {
    const [src, setSrc] = useState('');
    useEffect(() => {
        const url = URL.createObjectURL(file);
        setSrc(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    return (
        <div style={{ ...fotoCaixa, borderStyle: 'dashed' }}>
            <div style={fotoArea}>{src && <img src={src} alt="" style={fotoImg} />}</div>
            <button type="button" onClick={onRemover} title="Retirar" style={btnRemoverFoto}><DeleteIcon /></button>
            <div style={{ ...fotoLegenda, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
        </div>
    );
}

/* Com verificacaoId as fotografias gravam-se logo; sem ele (verificação nova)
   ficam em «pendentes» e o formulário envia-as depois de a criar. */
function RegistoFotografico({ verificacaoId, pendentes = [], setPendentes, onAlterado }) {
    const [fotos, setFotos] = useState([]);
    const [progresso, setProgresso] = useState('');
    const [aviso, setAviso] = useState('');
    const inputRef = useRef(null);

    const carregar = useCallback(async () => {
        if (!verificacaoId) return;
        try {
            const r = await api.get(`/verificacoes-obra/${verificacaoId}/fotos`);
            setFotos(r.data || []);
        } catch { setAviso('Erro ao carregar as fotografias.'); }
    }, [verificacaoId]);
    useEffect(() => { carregar(); }, [carregar]);

    const total = verificacaoId ? fotos.length : pendentes.length;

    const escolher = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        const livres = MAX_FOTOS - total;
        if (livres <= 0) { setAviso(`Máximo de ${MAX_FOTOS} fotografias por verificação.`); return; }
        const lote = files.slice(0, livres);
        const foraDoLimite = files.length - lote.length;
        const avisoLimite = foraDoLimite ? `${foraDoLimite} ficaram de fora (máximo ${MAX_FOTOS}).` : '';
        if (!verificacaoId) {
            setPendentes(p => [...p, ...lote]);
            setAviso(avisoLimite);
            return;
        }
        setAviso('');
        const r = await enviarFotos(verificacaoId, lote, setProgresso);
        setAviso([...r.falhas, avisoLimite].filter(Boolean).join(' · '));
        await carregar();
        if (r.enviadas && onAlterado) onAlterado();
    };

    const apagar = async (foto) => {
        if (!window.confirm('Remover esta fotografia?')) return;
        try {
            await api.delete(`/verificacoes-obra/fotos/${foto.id}`);
            setFotos(lista => lista.filter(f => f.id !== foto.id));
            if (onAlterado) onAlterado();
        } catch { setAviso('Erro ao remover a fotografia.'); }
    };

    const legendar = async (foto, legenda) => {
        try {
            await api.patch(`/verificacoes-obra/fotos/${foto.id}`, { legenda });
            setFotos(lista => lista.map(f => (f.id === foto.id ? { ...f, legenda } : f)));
        } catch { setAviso('Erro ao gravar a legenda.'); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {total ? `${total} fotografia${total === 1 ? '' : 's'}` : 'Ainda sem fotografias.'}
                    {!verificacaoId && total > 0 ? ' — carregadas ao guardar' : ''}
                </span>
                <button data-liquid-secondary="true" type="button" onClick={() => inputRef.current?.click()} disabled={!!progresso} style={btnSec}>
                    <LiquidText>{progresso || '📷 Juntar fotografias'}</LiquidText>
                </button>
                <input ref={inputRef} type="file" accept="image/*" multiple onChange={escolher} style={{ display: 'none' }} />
            </div>
            {aviso && <div style={{ color: '#b45309', fontSize: '0.78rem', marginBottom: 8 }}>{aviso}</div>}
            {total > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                    {verificacaoId
                        ? fotos.map(f => <Miniatura key={f.id} foto={f} onApagar={apagar} onLegenda={legendar} />)
                        : pendentes.map((file, i) => (
                            <PreviewLocal key={`${file.name}-${file.lastModified}-${i}`} file={file}
                                onRemover={() => setPendentes(p => p.filter((_, j) => j !== i))} />
                        ))}
                </div>
            )}
        </div>
    );
}

// Acesso direto a partir do cartão, sem abrir a check-list.
function FotosModal({ verificacao, onClose, onAlterado }) {
    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 780 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <div>
                        <h3 style={{ margin: 0 }}>Registo fotográfico</h3>
                        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                            {nomeObra(verificacao)}
                            {verificacao.relatorio_num ? ` · ${verificacao.relatorio_num}` : ''}
                            {' · as fotografias entram no PDF da verificação'}
                        </p>
                    </div>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Fechar</button>
                </div>
                <RegistoFotografico verificacaoId={verificacao.id} onAlterado={onAlterado} />
            </div>
        </LiquidDialog>
    );
}

/* ---------- Formulário da verificação ---------- */
function VerificacaoModal({ verificacao, ano, trimestre, itens, obras, trabalhadores, onClose, onSaved }) {
    const novo = !verificacao?.id;
    const [checklist, setChecklist] = useState(() => {
        if (verificacao?.checklist && Object.keys(verificacao.checklist).length) return verificacao.checklist;
        return itens.filter(i => !i.grupo).reduce((acc, i) => {
            acc[`item${i.id}`] = { status: 'C', obs: '' };
            return acc;
        }, {});
    });
    const [form, setForm] = useState({
        data_verificacao: verificacao?.data_verificacao ? verificacao.data_verificacao.substring(0, 10)
            : new Date().toISOString().substring(0, 10),
        obra_id: verificacao?.obra_id || '',
        obra_descricao: verificacao?.obra_descricao || '',
        verificado_por: verificacao?.verificado_por || '',
        observacoes: verificacao?.observacoes || '',
    });
    const [guardando, setGuardando] = useState(false);
    const [progresso, setProgresso] = useState('');
    const [pendentes, setPendentes] = useState([]); // fotografias de uma verificação ainda por gravar
    const [err, setErr] = useState('');

    const temNC = Object.values(checklist).some(c => c.status === 'NC');
    const [conforme, setConforme] = useState(verificacao ? verificacao.resultado_conforme : true);
    useEffect(() => { setConforme(!temNC); }, [temNC]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setItem = (key, campo, valor) =>
        setChecklist(c => ({ ...c, [key]: { ...c[key], [campo]: valor } }));

    // A secção documental repete-se entre obras visitadas no mesmo período —
    // copiar a última poupa responder duas vezes ao mesmo.
    const copiarUltima = async () => {
        try {
            const r = await api.get('/verificacoes-obra/ultima');
            if (!r.data) { setErr('Ainda não há nenhuma verificação para copiar.'); return; }
            if (r.data.checklist && Object.keys(r.data.checklist).length) setChecklist(r.data.checklist);
            if (r.data.verificado_por) set('verificado_por', r.data.verificado_por);
            setErr('');
        } catch { setErr('Erro ao copiar a última verificação.'); }
    };

    const guardar = async (comPdf) => {
        if (!form.obra_id && !form.obra_descricao.trim()) { setErr('Indique a obra verificada.'); return; }
        setGuardando(true); setErr('');
        try {
            const payload = {
                ...form, ano, trimestre, checklist, resultado_conforme: conforme,
                nome_verificador: trabalhadores.find(t => String(t.id) === String(form.verificado_por))?.nome || null,
                obra_id: form.obra_id || null,
            };
            const r = novo
                ? await api.post('/verificacoes-obra', payload)
                : await api.put(`/verificacoes-obra/${verificacao.id}`, payload);
            // Já gravada: daqui para baixo uma falha não pode deixar o formulário
            // aberto como «novo» (um segundo Guardar duplicava a verificação).
            const falhas = novo && pendentes.length
                ? (await enviarFotos(r.data.id, pendentes, setProgresso)).falhas : [];
            if (comPdf) {
                try {
                    const pdf = await api.get(`/verificacoes-obra/${r.data.id}/relatorio`, { responseType: 'blob', timeout: 60000 });
                    window.open(window.URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' })), '_blank');
                } catch { falhas.push('O PDF não foi gerado — abra-o no cartão da verificação.'); }
            }
            if (falhas.length) alert(`A verificação foi guardada, mas:\n\n${falhas.join('\n')}`);
            onSaved(); onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar a verificação.');
        } finally { setGuardando(false); setProgresso(''); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 940 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <h3 style={{ margin: 0 }}>
                        Check-list de verificação de obra — {trimestre}.º trimestre de {ano}
                    </h3>
                    {novo && <button data-liquid-secondary="true" onClick={copiarUltima} style={btnSec}><LiquidText>⧉ Copiar da última</LiquidText></button>}
                </div>
                <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.85rem' }}>
                    Verificação trimestral dos trabalhos em curso (P_IM_02), na vertente de qualidade e segurança.
                </p>

                <div data-liquid-grid="true" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data da visita</label>
                        <input type="date" value={form.data_verificacao}
                            onChange={e => set('data_verificacao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Obra verificada</label>
                        <select value={form.obra_id} onChange={e => set('obra_id', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">— escolher obra —</option>
                            {obras.map(o => (
                                <option key={o.id} value={o.id}>{[o.posicao, o.descricao, o.cliente].filter(Boolean).join(' · ')}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Verificado por</label>
                        <select value={form.verificado_por} onChange={e => set('verificado_por', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">— selecionar —</option>
                            {trabalhadores.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                        </select>
                    </div>
                </div>

                {!form.obra_id && (
                    <div style={{ marginBottom: 12 }}>
                        <label data-liquid-label="true" style={lbl}>Ou indique a obra manualmente</label>
                        <input value={form.obra_descricao} onChange={e => set('obra_descricao', e.target.value)}
                            placeholder="se a obra não estiver na lista" style={{ ...inp, width: '100%' }} />
                    </div>
                )}

                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 10px', fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                        <div style={{ flex: 1 }}>ITEM VERIFICADO</div>
                        <div style={{ width: 130, textAlign: 'center' }}>NA · C · NC</div>
                        <div style={{ width: 180 }}>OBSERVAÇÕES</div>
                    </div>

                    {itens.map((item, idx) => item.grupo ? (
                        <div key={`g${idx}`} style={{ background: '#f1f5f9', padding: '6px 10px', fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>
                            {item.grupo}
                        </div>
                    ) : (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ flex: 1, fontSize: '0.82rem' }}><LiquidText>{item.texto}</LiquidText></div>
                            <div style={{ width: 130, display: 'flex', gap: 4, justifyContent: 'center' }}>
                                {['NA', 'C', 'NC'].map(s => {
                                    const ativo = checklist[`item${item.id}`]?.status === s;
                                    const cor = s === 'C' ? '#16a34a' : s === 'NC' ? '#dc2626' : '#64748b';
                                    return (
                                        <button key={s} onClick={() => setItem(`item${item.id}`, 'status', s)}
                                            style={{
                                                width: 38, padding: '4px 0', borderRadius: 6, cursor: 'pointer',
                                                fontSize: '0.72rem', fontWeight: 700,
                                                border: `1px solid ${ativo ? cor : '#cbd5e1'}`,
                                                background: ativo ? cor : '#fff', color: ativo ? '#fff' : '#64748b',
                                            }}>{s}</button>
                                    );
                                })}
                            </div>
                            <input value={checklist[`item${item.id}`]?.obs || ''}
                                onChange={e => setItem(`item${item.id}`, 'obs', e.target.value)}
                                style={{ ...inp, width: 180, fontSize: '0.8rem', padding: '5px 8px' }} />
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: 14 }}>
                    <label data-liquid-label="true" style={lbl}>Observações gerais</label>
                    <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
                        rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                <div style={{ marginTop: 14 }}>
                    <label data-liquid-label="true" style={lbl}>Registo fotográfico</label>
                    <RegistoFotografico verificacaoId={verificacao?.id} pendentes={pendentes}
                        setPendentes={setPendentes} onAlterado={onSaved} />
                </div>

                <div style={{
                    marginTop: 14, padding: '10px 14px', borderRadius: 8,
                    background: conforme ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${conforme ? '#bbf7d0' : '#fecaca'}`,
                }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={conforme} onChange={e => setConforme(e.target.checked)} />
                        <span style={{ fontWeight: 700, color: conforme ? '#166534' : '#991b1b' }}>
                            {conforme ? 'Verificação conforme' : 'Não conforme'}
                        </span>
                    </label>
                    {!conforme && (
                        <p style={{ margin: '6px 0 0 28px', fontSize: '0.78rem', color: '#991b1b' }}>
                            O P_IM_02 remete para o PR.GSI.03: abra a não conformidade no separador
                            «Não Conformidades» e ligue-a a esta obra.
                        </p>
                    )}
                </div>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-secondary="true" onClick={() => guardar(false)} disabled={guardando} style={btnSec}>
                        {guardando ? (progresso || 'A guardar…') : 'Guardar'}
                    </button>
                    <button data-liquid-primary="true" onClick={() => guardar(true)} disabled={guardando} style={btnPri}>
                        <LiquidText>{guardando ? (progresso || 'A guardar…') : '📄 Guardar e gerar PDF'}</LiquidText>
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const VerificacaoObras = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [verificacoes, setVerificacoes] = useState([]);
    const [itens, setItens] = useState([]);
    const [obras, setObras] = useState([]);
    const [trabalhadores, setTrabalhadores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editar, setEditar] = useState(null); // { verificacao, trimestre }
    const [fotosDe, setFotosDe] = useState(null); // verificação com o registo fotográfico aberto

    useEffect(() => {
        api.get('/verificacoes-obra/checklist').then(r => setItens(r.data || [])).catch(() => { });
        api.get('/obras').then(r => setObras(r.data || [])).catch(() => { });
        api.get('/trabalhadores')
            .then(r => setTrabalhadores((r.data || []).filter(t => t.ativo !== false)))
            .catch(() => { });
    }, []);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [rv, ra] = await Promise.all([
                api.get('/verificacoes-obra', { params: { ano } }),
                api.get('/verificacoes-obra/anos'),
            ]);
            setVerificacoes(rv.data || []);
            setAnosExistentes(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const trimestreAtual = Math.floor(new Date().getMonth() / 3) + 1;
    const doTrimestre = (t) => verificacoes.filter(v => v.trimestre === t);

    const abrirPdf = async (v) => {
        try {
            const r = await api.get(`/verificacoes-obra/${v.id}/relatorio`, { responseType: 'blob', timeout: 60000 });
            window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
        } catch { alert('Erro ao gerar o PDF.'); }
    };

    const eliminar = async (v) => {
        if (!window.confirm('Eliminar esta verificação?')) return;
        try { await api.delete(`/verificacoes-obra/${v.id}`); carregar(); }
        catch { alert('Erro ao eliminar.'); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                <div>
                    <label data-liquid-label="true" style={lbl}>Ano</label>
                    <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                        {anos.map(a => <option key={a} value={a}>{a}{anosExistentes.includes(a) ? '' : ' (vazio)'}</option>)}
                    </select>
                </div>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem', flex: 1, minWidth: 240 }}>
                    Verificação trimestral dos trabalhos em curso, na vertente de qualidade
                    e segurança (P_IM_02). Um registo por obra visitada.
                </p>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14 }}>
                    {TRIMESTRES.map(t => {
                        const lista = doTrimestre(t.n);
                        const futuro = ano > anoAtual || (ano === anoAtual && t.n > trimestreAtual);
                        const emFalta = !lista.length && !futuro;
                        const cor = lista.length ? { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534' }
                            : emFalta ? { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' }
                                : { bg: '#f8fafc', bd: '#e2e8f0', fg: '#94a3b8' };
                        return (
                            <div key={t.n} style={{ background: cor.bg, border: `1px solid ${cor.bd}`, borderRadius: 12, padding: 14 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <strong style={{ fontSize: '0.95rem', color: '#1e293b' }}><LiquidText>{t.label}</LiquidText></strong>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t.meses}</span>
                                </div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: cor.fg, margin: '6px 0 10px' }}>
                                    {lista.length ? `${lista.length} verificação(ões)`
                                        : emFalta ? 'Trimestre sem verificação' : 'Ainda por decorrer'}
                                </div>

                                {lista.map(v => (
                                    <div key={v.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                            <strong style={{ fontSize: '0.82rem' }}>{nomeObra(v)}</strong>
                                            <span data-liquid-tone={liquidTone(v.resultado_conforme ? '#dcfce7' : '#fee2e2')} data-liquid-badge="true" style={{
                                                ...badge, fontSize: '0.68rem',
                                                background: v.resultado_conforme ? '#dcfce7' : '#fee2e2',
                                                color: v.resultado_conforme ? '#166534' : '#991b1b',
                                            }}>{v.resultado_conforme ? 'Conforme' : 'NC'}</span>
                                        </div>
                                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
                                            {v.data_verificacao ? new Date(v.data_verificacao).toLocaleDateString('pt-PT') : '—'}
                                            {v.verificador_nome ? ` · ${v.verificador_nome.split(' ')[0]}` : ''}
                                            {v.relatorio_num ? ` · ${v.relatorio_num}` : ''}
                                        </div>
                                        <div data-liquid-actions="true" style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                                            <button data-liquid-secondary="true" onClick={() => abrirPdf(v)} style={{ ...btnSec, ...mini, marginLeft: 0 }}><LiquidText>📄</LiquidText></button>
                                            <button data-liquid-secondary="true" onClick={() => setFotosDe(v)} title="Registo fotográfico"
                                                style={{ ...btnSec, ...mini, marginLeft: 0 }}><LiquidText>📷</LiquidText>{v.n_fotos ? ` ${v.n_fotos}` : ''}</button>
                                            <button data-liquid-secondary="true" onClick={() => setEditar({ verificacao: v, trimestre: t.n })} style={{ ...btnSec, ...mini, marginLeft: 0 }}><LiquidText>✎</LiquidText></button>
                                            <button data-liquid-secondary="true" onClick={() => eliminar(v)} style={{ ...btnSec, ...mini, marginLeft: 0, color: '#dc2626' }}><DeleteIcon /></button>
                                        </div>
                                    </div>
                                ))}

                                <button data-liquid-secondary="true" onClick={() => setEditar({ verificacao: null, trimestre: t.n })}
                                    style={{ ...btnSec, width: '100%', marginTop: 4 }}>
                                    ＋ Verificação
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {editar && (
                <VerificacaoModal
                    verificacao={editar.verificacao}
                    ano={ano}
                    trimestre={editar.trimestre}
                    itens={itens}
                    obras={obras}
                    trabalhadores={trabalhadores}
                    onClose={() => setEditar(null)}
                    onSaved={carregar} />
            )}

            {fotosDe && <FotosModal verificacao={fotosDe} onClose={() => setFotosDe(null)} onAlterado={carregar} />}
        </div>
    );
};

const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', fontSize: '0.78rem' };
const fotoCaixa = { position: 'relative', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' };
const fotoArea = { height: 110, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', color: '#94a3b8' };
const fotoImg = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const fotoLegenda = { width: '100%', border: 'none', borderTop: '1px solid #f1f5f9', padding: '5px 7px', fontSize: '0.74rem', boxSizing: 'border-box', outline: 'none' };
const btnRemoverFoto = { position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.65)', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', lineHeight: '22px', padding: 0 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };

export default VerificacaoObras;
